import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, EyeOff, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, fmtTime } from "@/lib/api";
import { card, Empty, inputCls } from "@/components/admin/kit";

export function useFetch(api, path) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { if (path) { api.get(path).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); } }, [api, path]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

export async function download(api, path, name) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = name; a.click();
  } catch (e) { toast.error("Could not download the PDF"); }
}

export const thisMonth = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 7);
const FL = { unanswered_3: "3 unanswered", pressed_9: "Pressed 9", refill_overdue: "Refill overdue" };

export function FlagsPanel({ api, path, seenPath, canSeen = true, testid = "flags-panel" }) {
  const [list, reload] = useFetch(api, path);
  const seen = async (id) => { try { await api.post(seenPath(id)); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={card} data-testid={testid}>
      <p className="mb-2 flex items-center gap-2 font-bold"><Flag size={16} className="text-red-600" /> Flags {list ? `(${list.length})` : ""}</p>
      {!list ? <Empty>Loading…</Empty> : !list.length ? <Empty>No new flags.</Empty> : list.map((f) => (
        <div key={f.id} className="flex flex-wrap items-center gap-2 border-t py-2 text-sm" data-testid={`flag-${f.type}-${f.patient_id}`}>
          <span className="rounded-full bg-red-100 px-2 text-xs font-bold text-red-700">{FL[f.type] || f.type}</span>
          <span className="flex-1">{f.text}{f.org ? <span className="text-muted-foreground"> · {f.org}</span> : null}</span>
          <span className="text-xs text-muted-foreground">{fmtTime(f.created_at)}</span>
          {canSeen && <Button size="sm" variant="outline" onClick={() => seen(f.id)} data-testid={`flag-seen-${f.id}`}><EyeOff size={14} /> Mark seen</Button>}
        </div>
      ))}
    </div>
  );
}

const ROWS = [["Active patients", "active_patients"], ["New this month", "new_this_month"], ["Calls made", "calls_made"], ["Answered", "answered_pct", "%"],
  ["Listened fully", "listened_fully_pct", "%"], ["Pressed 9", "pressed_9"], ["Refills due", "refills_due"], ["Returned for follow-up", "returned_for_followup"], ["Stopped", "stopped"]];

export function MonthlyReport({ api, path, title }) {
  const [month, setMonth] = useState(thisMonth());
  const q = `${path}${path.includes("?") ? "&" : "?"}month=${month}`;
  const [r] = useFetch(api, path ? q : null);
  return (
    <div className={`${card} grid gap-3`} data-testid="monthly-report">
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-auto font-bold">{title || "Monthly report"}</p>
        <input type="month" className={`${inputCls.replace("w-full ", "")} w-40`} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" />
        <Button size="sm" variant="outline" onClick={() => download(api, `${q}&fmt=pdf`, `OnDose-report-${month}.pdf`)} data-testid="report-pdf-btn"><Download size={14} /> PDF</Button>
      </div>
      <p className="text-xs text-muted-foreground">Counts only real reminder calls — never simulator or test calls.</p>
      {r && <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">{ROWS.map(([l, k, u]) => (
        <div key={k} className="rounded-2xl bg-[var(--lav)]/50 p-3" data-testid={`report-${k}`}><p className="text-xs text-muted-foreground">{l}</p><p className="text-2xl font-bold">{r[k]}{u || ""}</p></div>))}</div>}
      {r?.doctors?.length > 0 && (
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="report-doctors"><thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Doctor</th>{ROWS.map(([l]) => <th key={l} className="px-1">{l}</th>)}</tr></thead>
          <tbody>{r.doctors.map((d) => <tr key={d.id} className="border-t"><td className="py-1 font-semibold">{d.name}</td>{ROWS.map(([, k, u]) => <td key={k} className="px-1">{d[k]}{u || ""}</td>)}</tr>)}</tbody></table></div>
      )}
    </div>
  );
}

export function SampleReport({ api }) {
  const [r] = useFetch(api, "/admin/sample-report");
  if (!r) return <div className={card}><Empty>Loading…</Empty></div>;
  return (
    <div className={`${card} relative grid gap-3 overflow-hidden border-2 border-red-500`} data-testid="sample-report">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><span className="rotate-[-20deg] text-8xl font-black text-red-500/10">SAMPLE</span></div>
      <p className="rounded-xl bg-red-600 px-3 py-2 text-center text-sm font-bold text-white" data-testid="sample-banner">SAMPLE REPORT — made-up numbers for demonstration only</p>
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-auto font-bold">{r.org} (SAMPLE) · {r.month} · 300 patients · 3 doctors</p>
        <Button size="sm" variant="outline" onClick={() => download(api, "/admin/sample-report?fmt=pdf", `OnDose-SAMPLE-report-${r.month}.pdf`)} data-testid="sample-pdf-btn"><Download size={14} /> PDF</Button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">{ROWS.map(([l, k, u]) => (
        <div key={k} className="rounded-2xl bg-[var(--lav)]/50 p-3" data-testid={`sample-${k}`}><p className="text-xs text-muted-foreground">{l}</p><p className="text-2xl font-bold">{r[k].toLocaleString("en-IN")}{u || ""}</p></div>))}</div>
      <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="sample-doctors"><thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Doctor</th>{ROWS.map(([l]) => <th key={l} className="px-1">{l}</th>)}</tr></thead>
        <tbody>{r.doctors.map((d) => <tr key={d.id} className="border-t"><td className="py-1 font-semibold">{d.name}</td>{ROWS.map(([, k, u]) => <td key={k} className="px-1">{d[k].toLocaleString("en-IN")}{u || ""}</td>)}</tr>)}</tbody></table></div>
    </div>
  );
}

export function AuditList({ api, path }) {
  const [list] = useFetch(api, path);
  return (
    <div className={card} data-testid="audit-log">
      {!list ? <Empty>Loading…</Empty> : !list.length ? <Empty>No changes recorded yet.</Empty> : list.map((x) => (
        <p key={x.id} className="border-t py-2 text-sm"><b>{fmtTime(x.at)}</b> · {x.who} <span className="text-muted-foreground">({(x.role || "").replace("_", " ")})</span> · <span className="font-semibold text-[var(--violet)]">{x.action}</span> · {x.item}
          {x.after != null && <span className="text-xs text-muted-foreground"> → {typeof x.after === "object" ? JSON.stringify(x.after) : String(x.after)}</span>}</p>
      ))}
    </div>
  );
}
